import { useEffect, useState } from 'react'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  ClipboardPaste,
  Upload,
  CheckCircle2,
  AlertTriangle,
  Plus,
  X,
  Edit3,
  Users,
  Building2,
  Sparkles,
} from 'lucide-react'
import {
  apiService,
  CustomField,
  CustomFieldOption,
  CompanyCustomField,
  CompanyCustomFieldOption,
} from '@/services/api'
import { useToast } from '@/hooks/use-toast'

type Tab = 'paste' | 'file'
type Entity = 'contact' | 'company'

type StandardField = 'email' | 'phone' | 'linkedin_url'
type Modification =
  | { kind: 'standard'; field: StandardField; value: string }
  | { kind: 'custom'; fieldId: number; value: string }

type CustomFieldAny = (CustomField | CompanyCustomField) & {
  options?: (CustomFieldOption | CompanyCustomFieldOption)[]
}

type FoundItem = {
  id: number
  label: string // "Prénom Nom" pour contact, "Nom" pour entreprise
  detail?: string // email pour contact, matchedBy pour entreprise
}

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
}

/**
 * Modal "Actions multiples" — Design A (2 volets côte à côte)
 * Onglets Contacts / Entreprises en haut. Volet gauche : cible (coller ou fichier),
 * volet droit : modifications empilables (uniquement champs perso select+radio).
 */
export default function BulkActionsModal({ open, onOpenChange }: Props) {
  const { toast } = useToast()

  const [entity, setEntity] = useState<Entity>('contact')

  // Volet gauche : cible
  const [tab, setTab] = useState<Tab>('paste')
  const [pasted, setPasted] = useState('')
  const [found, setFound] = useState<FoundItem[]>([])
  const [notFound, setNotFound] = useState<string[]>([])
  const [suggestions, setSuggestions] = useState<
    { query: string; candidates: { id: number; name: string; score: number }[] }[]
  >([])
  const [isMatching, setIsMatching] = useState(false)
  const [showNotFound, setShowNotFound] = useState(false)

  // Volet droit : modifications
  const [modifications, setModifications] = useState<Modification[]>([])
  const [customFields, setCustomFields] = useState<CustomFieldAny[]>([])

  const [isApplying, setIsApplying] = useState(false)

  // Reset et chargement des champs perso quand on ouvre ou change d'entité
  useEffect(() => {
    if (!open) return
    setTab('paste')
    setPasted('')
    setFound([])
    setNotFound([])
    setSuggestions([])
    setShowNotFound(false)
    setModifications([])
  }, [open, entity])

  useEffect(() => {
    if (!open) return
    const load = async () => {
      if (entity === 'contact') {
        const res = await apiService.getContactCustomFields()
        if (res.success && res.data) setCustomFields(res.data)
      } else {
        const res = await apiService.getCompanyCustomFieldsWithUsage()
        if (res.success && res.data) setCustomFields(res.data)
      }
    }
    load()
  }, [open, entity])

  // Champs perso filtrés : uniquement select + radio (décision produit)
  const eligibleCustomFields = customFields.filter(
    (f) => f.type === 'select' || f.type === 'radio',
  )

  // Parse : sépare par séparateurs multiples, nettoie caractères invisibles, dédup
  const parseLines = (raw: string): string[] => {
    return Array.from(
      new Set(
        raw
          // Sépare : retour ligne (Unix + Windows), virgule, point-virgule, tabulation
          .split(/[\n\r\t,;]+/)
          .map((s) =>
            s
              // Retire les caractères invisibles courants (BOM, zero-width, non-breaking space…)
              .replace(/[\u200B-\u200D\uFEFF\u00A0]/g, '')
              .trim(),
          )
          .filter((s) => s.length > 0),
      ),
    )
  }

  // Contacts : on ne garde que les valeurs qui ressemblent à un email
  const parseEmails = (raw: string): string[] =>
    parseLines(raw)
      .map((s) => s.toLowerCase())
      .filter((s) => s.includes('@'))

  const runMatch = async (raw: string) => {
    if (entity === 'contact') {
      const emails = parseEmails(raw)
      if (emails.length === 0) {
        setFound([])
        setNotFound([])
        setSuggestions([])
        return
      }
      setIsMatching(true)
      try {
        const res = await apiService.matchContactEmails(emails)
        if (res.success && res.data) {
          setFound(
            res.data.found.map((c) => ({
              id: c.id,
              label: `${c.first_name} ${c.last_name}`,
              detail: c.email,
            })),
          )
          setNotFound(res.data.notFound)
          setSuggestions([])
        } else {
          toast({
            variant: 'destructive',
            title: 'Erreur',
            description: res.error || 'Impossible de rechercher les contacts',
          })
        }
      } finally {
        setIsMatching(false)
      }
    } else {
      const queries = parseLines(raw)
      if (queries.length === 0) {
        setFound([])
        setNotFound([])
        setSuggestions([])
        return
      }
      setIsMatching(true)
      try {
        const res = await apiService.matchCompanies(queries)
        if (res.success && res.data) {
          setFound(
            res.data.found.map((c) => ({
              id: c.id,
              label: c.name,
              detail:
                c.matchedBy === 'siret'
                  ? 'SIRET'
                  : c.matchedBy === 'name'
                    ? 'nom'
                    : 'fuzzy',
            })),
          )
          setNotFound(res.data.notFound)
          setSuggestions(res.data.suggestions)
        } else {
          toast({
            variant: 'destructive',
            title: 'Erreur',
            description: res.error || 'Impossible de rechercher les entreprises',
          })
        }
      } finally {
        setIsMatching(false)
      }
    }
  }

  const handlePasteChange = (v: string) => {
    setPasted(v)
    if ((handlePasteChange as any)._t) clearTimeout((handlePasteChange as any)._t)
    ;(handlePasteChange as any)._t = setTimeout(() => runMatch(v), 400)
  }

  const handleFile = async (file: File) => {
    const text = await file.text()
    setPasted(text)
    runMatch(text)
  }

  // Accepter une suggestion fuzzy → ajouter aux "found" et retirer des suggestions
  const acceptSuggestion = (query: string, cand: { id: number; name: string }) => {
    setFound((prev) => {
      if (prev.some((f) => f.id === cand.id)) return prev
      return [
        ...prev,
        {
          id: cand.id,
          label: cand.name,
          detail: 'sélectionné',
        },
      ]
    })
    setSuggestions((prev) => prev.filter((s) => s.query !== query))
  }

  const addModification = () => {
    if (entity === 'contact') {
      setModifications((prev) => [
        ...prev,
        { kind: 'standard', field: 'email', value: '' },
      ])
    } else {
      // Pour les entreprises, on ne propose que des champs perso (pas de standards)
      const first = eligibleCustomFields[0]
      if (first) {
        setModifications((prev) => [
          ...prev,
          { kind: 'custom', fieldId: first.id, value: '' },
        ])
      } else {
        toast({
          variant: 'default',
          title: 'Aucun champ disponible',
          description:
            'Aucun champ personnalisé de type liste déroulante / bouton radio pour les entreprises.',
        })
      }
    }
  }
  const removeModification = (index: number) => {
    setModifications((prev) => prev.filter((_, i) => i !== index))
  }
  const updateModification = (index: number, mod: Modification) => {
    setModifications((prev) => prev.map((m, i) => (i === index ? mod : m)))
  }

  // Une valeur est "vide" si elle est absente ou uniquement composée d'espaces.
  // Les modifications vides ne doivent JAMAIS être envoyées : elles écraseraient
  // le champ existant en base par NULL (data loss).
  const isEmptyValue = (m: Modification) =>
    typeof m.value !== 'string' || m.value.trim() === ''
  const hasEmptyModification = modifications.some(isEmptyValue)
  const nonEmptyModifications = modifications.filter((m) => !isEmptyValue(m))

  const canApply =
    found.length > 0 &&
    modifications.length > 0 &&
    !hasEmptyModification &&
    !isApplying

  const apply = async () => {
    if (!canApply) return
    setIsApplying(true)
    try {
      if (entity === 'contact') {
        const standard: Record<string, string | number | null> = {}
        const custom: { id: number; value: string }[] = []
        // Sécurité : on ne prend que les modifications avec une valeur non vide,
        // même si le bouton Apply est déjà bloqué en amont (double garde).
        for (const m of nonEmptyModifications) {
          if (m.kind === 'standard') standard[m.field] = m.value
          else custom.push({ id: m.fieldId, value: m.value })
        }
        const res = await apiService.bulkUpdateContacts({
          ids: found.map((f) => f.id),
          standard: Object.keys(standard).length > 0 ? standard : undefined,
          custom_fields: custom.length > 0 ? custom : undefined,
        } as any)
        if (res.success) {
          toast({
            title: '✅ Modifications appliquées',
            description: `${found.length} contact${found.length > 1 ? 's' : ''} mis à jour`,
            variant: 'success',
          })
          // Les fiches porteuses d'un compte UP-SPOT gardent leur adresse de connexion.
          const avertissement = (res.data as any)?.avertissement
          if (avertissement) {
            toast({ variant: 'destructive', title: 'Adresses non modifiées', description: avertissement })
          }
          onOpenChange(false)
        } else {
          toast({
            variant: 'destructive',
            title: 'Erreur',
            description: res.error || 'Impossible d\'appliquer les modifications',
          })
        }
      } else {
        const custom: { id: number; value: string }[] = []
        for (const m of nonEmptyModifications) {
          if (m.kind === 'custom') custom.push({ id: m.fieldId, value: m.value })
        }
        const res = await apiService.bulkUpdateCompanies({
          ids: found.map((f) => f.id),
          custom_fields: custom.length > 0 ? custom : undefined,
        })
        if (res.success) {
          toast({
            title: '✅ Modifications appliquées',
            description: `${found.length} entreprise${found.length > 1 ? 's' : ''} mise${found.length > 1 ? 's' : ''} à jour`,
            variant: 'success',
          })
          onOpenChange(false)
        } else {
          toast({
            variant: 'destructive',
            title: 'Erreur',
            description: res.error || 'Impossible d\'appliquer les modifications',
          })
        }
      }
    } finally {
      setIsApplying(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[900px] p-0 gap-0 overflow-hidden rounded-2xl sm:rounded-2xl">
        {/* En-tête */}
        <div className="flex items-center gap-3 px-6 py-4 border-b border-slate-100">
          <div className="w-10 h-10 rounded-lg bg-blue-100 text-blue-600 flex items-center justify-center shrink-0">
            <Edit3 className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <DialogTitle className="text-lg font-bold text-slate-800">
              Actions multiples
            </DialogTitle>
            <p className="text-xs text-slate-500">
              Modifiez plusieurs champs d'un coup sur une liste de{' '}
              {entity === 'contact' ? 'contacts' : 'entreprises'}
            </p>
          </div>
          {/* Onglet Contacts / Entreprises */}
          <div className="inline-flex p-0.5 rounded-lg bg-slate-100 text-sm font-medium">
            <button
              type="button"
              onClick={() => setEntity('contact')}
              className={`px-3 py-1.5 rounded-md inline-flex items-center gap-1.5 ${
                entity === 'contact'
                  ? 'bg-white shadow-sm text-blue-700'
                  : 'text-slate-500'
              }`}
            >
              <Users className="w-3.5 h-3.5" /> Contacts
            </button>
            <button
              type="button"
              onClick={() => setEntity('company')}
              className={`px-3 py-1.5 rounded-md inline-flex items-center gap-1.5 ${
                entity === 'company'
                  ? 'bg-white shadow-sm text-blue-700'
                  : 'text-slate-500'
              }`}
            >
              <Building2 className="w-3.5 h-3.5" /> Entreprises
            </button>
          </div>
        </div>

        {/* Corps 2 volets */}
        <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-slate-100">
          {/* Volet gauche : cible */}
          <div className="p-5 space-y-3">
            <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider">
              1. Cibler les {entity === 'contact' ? 'contacts' : 'entreprises'}
            </h3>
            <div className="inline-flex p-0.5 rounded-lg bg-slate-100 text-sm font-medium">
              <button
                type="button"
                onClick={() => setTab('paste')}
                className={`px-3 py-1 rounded-md inline-flex items-center gap-1.5 ${
                  tab === 'paste'
                    ? 'bg-white shadow-sm text-blue-700'
                    : 'text-slate-500'
                }`}
              >
                <ClipboardPaste className="w-3.5 h-3.5" /> Coller
              </button>
              <button
                type="button"
                onClick={() => setTab('file')}
                className={`px-3 py-1 rounded-md inline-flex items-center gap-1.5 ${
                  tab === 'file'
                    ? 'bg-white shadow-sm text-blue-700'
                    : 'text-slate-500'
                }`}
              >
                <Upload className="w-3.5 h-3.5" /> Fichier
              </button>
            </div>

            {tab === 'paste' && (
              <>
                <textarea
                  rows={6}
                  value={pasted}
                  onChange={(e) => handlePasteChange(e.target.value)}
                  className="w-full rounded-lg border border-slate-200 text-xs px-3 py-2 font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder={
                    entity === 'contact'
                      ? 'Collez ici des emails\n\nSéparateurs : retour ligne, virgule, point-virgule\n\ncontact1@exemple.com\ncontact2@exemple.com'
                      : 'Collez ici des noms d\'entreprise ou des SIRET\n\nSéparateurs : retour ligne, virgule, point-virgule\n\nISOVER\n12345678901234\nPAPREC (GROUPE)'
                  }
                />
                <p className="text-xs text-slate-400">
                  {entity === 'contact'
                    ? `${parseEmails(pasted).length} email${parseEmails(pasted).length > 1 ? 's' : ''} détecté${parseEmails(pasted).length > 1 ? 's' : ''}`
                    : `${parseLines(pasted).length} valeur${parseLines(pasted).length > 1 ? 's' : ''} · SIRET (14 chiffres) détectés automatiquement, le reste traité comme nom`}
                </p>
              </>
            )}

            {tab === 'file' && (
              <div className="rounded-xl border-2 border-dashed border-slate-300 p-6 text-center bg-slate-50/50">
                <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                <p className="text-sm text-slate-600 font-medium">
                  <label className="text-blue-600 font-semibold cursor-pointer hover:underline">
                    Choisir un fichier
                    <input
                      type="file"
                      accept=".txt,.csv"
                      className="hidden"
                      onChange={(e) => {
                        const f = e.target.files?.[0]
                        if (f) handleFile(f)
                      }}
                    />
                  </label>{' '}
                  ou glissez-le ici
                </p>
                <p className="text-xs text-slate-400 mt-1">
                  Formats : .txt, .csv (une valeur par ligne)
                </p>
              </div>
            )}

            {/* Résultat du matching */}
            {isMatching && (
              <p className="text-xs text-slate-500 italic">Recherche en cours…</p>
            )}
            {!isMatching && found.length === 0 && (notFound.length > 0 || suggestions.length > 0) && (
              <div className="rounded-lg bg-rose-50 ring-1 ring-rose-200 p-2.5 text-xs flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-rose-600 shrink-0" />
                <span className="text-slate-700">
                  <b>Aucun {entity === 'contact' ? 'contact' : 'entreprise'} trouvé</b>{' '}
                  · les {notFound.length + suggestions.length} valeur
                  {notFound.length + suggestions.length > 1 ? 's' : ''} saisie
                  {notFound.length + suggestions.length > 1 ? 's' : ''} ne correspond
                  {notFound.length + suggestions.length > 1 ? 'ent' : ''} à aucun
                  {' '}
                  {entity === 'contact' ? 'contact' : 'entreprise'} du CRM
                </span>
                <button
                  onClick={() => setShowNotFound((v) => !v)}
                  className="ml-auto text-rose-700 hover:underline shrink-0"
                >
                  {showNotFound ? 'masquer' : 'voir'}
                </button>
              </div>
            )}
            {!isMatching && found.length > 0 && (
              <div className="rounded-lg bg-emerald-50 ring-1 ring-emerald-200 p-2.5 text-xs flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                <span className="text-slate-700">
                  <b>{found.length}</b>{' '}
                  {entity === 'contact' ? 'contact' : 'entreprise'}
                  {found.length > 1 ? 's' : ''} sélectionné
                  {found.length > 1 ? 's' : ''}
                </span>
                {(notFound.length > 0 || suggestions.length > 0) && (
                  <>
                    <span className="text-slate-400">·</span>
                    <button
                      onClick={() => setShowNotFound((v) => !v)}
                      className="text-amber-700 hover:underline flex items-center gap-1"
                    >
                      <AlertTriangle className="w-3.5 h-3.5" />
                      {notFound.length + suggestions.length} non trouvé
                      {notFound.length + suggestions.length > 1 ? 's' : ''} ·{' '}
                      {showNotFound ? 'masquer' : 'voir'}
                    </button>
                  </>
                )}
              </div>
            )}

            {/* Panneau non trouvés + suggestions */}
            {showNotFound && (suggestions.length > 0 || notFound.length > 0) && (
              <div className="space-y-2 max-h-56 overflow-y-auto">
                {suggestions.map((s) => (
                  <div
                    key={s.query}
                    className="rounded-lg bg-amber-50 ring-1 ring-amber-200 p-2.5 space-y-1"
                  >
                    <div className="flex items-center gap-1.5 text-xs text-slate-700">
                      <Sparkles className="w-3.5 h-3.5 text-amber-600" />
                      <span className="italic">« {s.query} »</span>
                      <span className="text-slate-400">→ suggestions :</span>
                    </div>
                    <div className="space-y-1">
                      {s.candidates.map((c) => (
                        <button
                          key={c.id}
                          type="button"
                          onClick={() => acceptSuggestion(s.query, c)}
                          className="w-full flex items-center justify-between gap-2 text-xs bg-white ring-1 ring-slate-200 rounded px-2 py-1 hover:bg-blue-50 hover:ring-blue-300"
                        >
                          <span className="text-slate-700 truncate">
                            {c.name}
                          </span>
                          <span className="text-slate-400 font-mono shrink-0">
                            {Math.round(c.score * 100)}%
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
                {notFound.length > 0 && (
                  <div className="rounded-lg bg-rose-50 ring-1 ring-rose-200 p-2.5">
                    <p className="text-xs text-slate-600 mb-1 font-semibold">
                      Aucun match, à corriger manuellement :
                    </p>
                    <ul className="text-xs text-slate-700 space-y-0.5 font-mono">
                      {notFound.map((e) => (
                        <li key={e}>· {e}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Volet droit : modifications */}
          <div className="p-5 bg-slate-50/30 space-y-3">
            <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider">
              2. Modifications à appliquer
            </h3>

            {modifications.length === 0 && (
              <p className="text-xs text-slate-400 italic">
                Aucune modification définie.
              </p>
            )}

            <div className="space-y-2">
              {modifications.map((mod, i) => (
                <ModificationRow
                  key={i}
                  entity={entity}
                  mod={mod}
                  customFields={eligibleCustomFields}
                  isEmpty={isEmptyValue(mod)}
                  onChange={(m) => updateModification(i, m)}
                  onRemove={() => removeModification(i)}
                />
              ))}
              <button
                type="button"
                onClick={addModification}
                className="w-full py-2 rounded-lg border border-dashed border-slate-300 text-xs text-slate-500 hover:bg-white hover:border-blue-300 hover:text-blue-600 flex items-center justify-center gap-1"
              >
                <Plus className="w-3.5 h-3.5" /> Ajouter une modification
              </button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-slate-100 bg-slate-50">
          <p className="text-sm text-slate-600">
            <b className="text-slate-800">{found.length}</b>{' '}
            {entity === 'contact' ? 'contact' : 'entreprise'}
            {found.length > 1 ? 's' : ''} ·{' '}
            <b className="text-slate-800">{modifications.length}</b> modification
            {modifications.length > 1 ? 's' : ''}
            {hasEmptyModification && (
              <span className="ml-2 text-rose-600 font-medium">
                · valeur(s) manquante(s)
              </span>
            )}
          </p>
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isApplying}
            >
              Annuler
            </Button>
            <Button
              onClick={apply}
              disabled={!canApply}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {isApplying
                ? 'Application…'
                : `Appliquer sur ${found.length} ${entity === 'contact' ? 'contact' : 'entreprise'}${found.length > 1 ? 's' : ''}`}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

// ------------------------------------------------------------
// Ligne de modification (champ standard OU champ perso)
// ------------------------------------------------------------
function ModificationRow({
  entity,
  mod,
  customFields,
  isEmpty,
  onChange,
  onRemove,
}: {
  entity: Entity
  mod: Modification
  customFields: CustomFieldAny[]
  isEmpty: boolean
  onChange: (m: Modification) => void
  onRemove: () => void
}) {
  const currentKey =
    mod.kind === 'standard' ? `std:${mod.field}` : `cf:${mod.fieldId}`

  const handleFieldChange = (key: string) => {
    if (key.startsWith('std:')) {
      onChange({
        kind: 'standard',
        field: key.slice(4) as StandardField,
        value: '',
      })
    } else if (key.startsWith('cf:')) {
      onChange({
        kind: 'custom',
        fieldId: parseInt(key.slice(3), 10),
        value: '',
      })
    }
  }

  return (
    <div
      className={`rounded-lg bg-white p-3 space-y-2 ring-1 ${
        isEmpty ? 'ring-rose-300 bg-rose-50/40' : 'ring-slate-200'
      }`}
    >
      <div className="flex items-center gap-2">
        <Label className="text-xs text-slate-500 w-14 shrink-0">Champ</Label>
        <Select value={currentKey} onValueChange={handleFieldChange}>
          <SelectTrigger className="h-8 text-xs flex-1">
            <SelectValue placeholder="Choisir un champ…" />
          </SelectTrigger>
          <SelectContent>
            {entity === 'contact' && (
              <>
                <SelectItem value="std:email">Email</SelectItem>
                <SelectItem value="std:phone">Téléphone</SelectItem>
                <SelectItem value="std:linkedin_url">URL LinkedIn</SelectItem>
              </>
            )}
            {customFields.map((f) => (
              <SelectItem key={f.id} value={`cf:${f.id}`}>
                {f.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <button
          type="button"
          onClick={onRemove}
          className="text-slate-300 hover:text-rose-500 shrink-0"
          title="Retirer cette modification"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      <div className="flex items-center gap-2">
        <Label className="text-xs text-slate-500 w-14 shrink-0">Valeur</Label>
        <div className="flex-1">
          <ValueEditor mod={mod} customFields={customFields} onChange={onChange} />
        </div>
      </div>
      {isEmpty && (
        <div className="flex items-center gap-1.5 text-xs text-rose-600">
          <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
          <span>
            Valeur manquante — cette modification sera ignorée. Renseigne une
            valeur ou retire la ligne.
          </span>
        </div>
      )}
    </div>
  )
}

// ------------------------------------------------------------
// Éditeur de valeur adapté au type de champ
// ------------------------------------------------------------
function ValueEditor({
  mod,
  customFields,
  onChange,
}: {
  mod: Modification
  customFields: CustomFieldAny[]
  onChange: (m: Modification) => void
}) {
  if (mod.kind === 'custom') {
    const field = customFields.find((f) => f.id === mod.fieldId)
    if (!field) return null

    if (field.type === 'radio' && field.options && field.options.length > 0) {
      return (
        <div className="inline-flex p-0.5 rounded-lg bg-slate-100 text-sm font-medium">
          {field.options.map((o) => {
            const ov = o.value || o.label
            const sel = mod.value === ov
            const lower = o.label.trim().toLowerCase()
            const cls =
              lower === 'oui'
                ? 'bg-emerald-600 text-white'
                : lower === 'non'
                  ? 'bg-rose-500 text-white'
                  : 'bg-blue-600 text-white'
            return (
              <button
                key={o.id}
                type="button"
                onClick={() => onChange({ ...mod, value: ov })}
                className={`px-3 py-1 rounded-md ${sel ? cls : 'text-slate-500'}`}
              >
                {o.label}
              </button>
            )
          })}
        </div>
      )
    }
    if (field.type === 'select' && field.options && field.options.length > 0) {
      return (
        <Select
          value={mod.value}
          onValueChange={(v) => onChange({ ...mod, value: v })}
        >
          <SelectTrigger className="h-8 text-xs">
            <SelectValue placeholder="Sélectionner…" />
          </SelectTrigger>
          <SelectContent>
            {field.options.map((o) => (
              <SelectItem key={o.id} value={o.value || o.label}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )
    }
    return null
  }

  // Champs standards contact : email / phone / linkedin_url
  const placeholder =
    mod.field === 'email'
      ? 'contact@exemple.com'
      : mod.field === 'phone'
        ? '01 23 45 67 89'
        : 'https://linkedin.com/in/…'
  return (
    <Input
      type={mod.field === 'email' ? 'email' : 'text'}
      className="h-8 text-xs"
      value={mod.value}
      onChange={(e) => onChange({ ...mod, value: e.target.value })}
      placeholder={placeholder}
    />
  )
}
